import gql from "graphql-tag";
import * as Cookies from "es-cookie";

// Device-side copies of exams.
//   exam_local_progress  - latest answers of the exam being written, saved
//                          every second, so a close/reopen between server
//                          autosaves loses nothing. One exam at a time.
//   offline_submissions  - finished exams that could not reach the server,
//                          keyed by student + exam; each is submitted
//                          automatically once the network/server is back. A
//                          stuck one never blocks the next exam, and on a
//                          shared computer each student's stay separate.
const PROGRESS_KEY = "exam_local_progress";
const SUBMISSIONS_KEY = "offline_submissions";
const LEGACY_SUBMISSION_KEY = "offline_submission";
const LEGACY_AUTOSAVE_KEY = "offline_autosave";

export const OFFLINE_SUBMITTED_EVENT = "exam-offline-submitted";

const SUBMIT_SCHEDULE_TEST = gql`
  mutation ($params: FinishStudentScheduleExam, $uid: String) {
    finishStudentScheduleExam(params: $params, uid: $uid)
  }
`;
const IS_SCHEDULE_EXAM_SUBMITTED = gql`
  query ($mobile: String!, $schedule_exam_id: Int!) {
    isScheduleExamSubmitted(mobile: $mobile, schedule_exam_id: $schedule_exam_id)
  }
`;
const AUTO_SAVE = gql`
  mutation ($params: ExamautoSaveInput) {
    autoSaveExam(params: $params)
  }
`;
const GENERATE_EXAM_SESSION_RANKS = gql`
  mutation ($schedule_exam_id: Int!) {
    generateExamSessionRanks(schedule_exam_id: $schedule_exam_id) {
      schedule_exam_id
      ranked
    }
  }
`;

const readJSON = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
  }
};

const remove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {}
};

export const isNetworkError = (error) => {
  if (typeof navigator !== "undefined" && !navigator.onLine) return true;
  if (!error) return false;
  const msg = (error.message || "").toString();
  const netMsg = ((error.networkError && error.networkError.message) || "").toString();
  return (
    !!error.networkError ||
    msg.includes("Network error") ||
    msg.includes("Failed to fetch") ||
    netMsg.includes("Failed to fetch") ||
    netMsg.includes("Network error")
  );
};

// `autosaveParams` is the ExamautoSaveInput payload (mobile, exam_paper_id,
// questions_data, qsno, examtimer, uid, ...).
export const saveExamProgress = (autosaveParams) => {
  writeJSON(PROGRESS_KEY, { ...autosaveParams, saved_at: Date.now() });
};

// Local progress for this student + exam, or null.
export const getExamProgress = (mobile, examPaperId) => {
  const progress = readJSON(PROGRESS_KEY) || readJSON(LEGACY_AUTOSAVE_KEY);
  if (!progress) return null;
  if (progress.mobile != mobile) return null;
  if (parseInt(progress.exam_paper_id) !== parseInt(examPaperId)) return null;
  return progress;
};

export const clearExamProgress = () => {
  remove(PROGRESS_KEY);
  remove(LEGACY_AUTOSAVE_KEY);
};

// `params` is the FinishStudentScheduleExam payload, `autosave` the
// ExamautoSaveInput payload with the same answers (pushed to the server
// autosave before submitting).
const submissionKey = (params) =>
  `${params.mobile}:${parseInt(params.schedule_exam_id)}`;

// A submission that has not reached the server within this time is dropped.
const SUBMISSION_TTL_MS = 24 * 60 * 60 * 1000;

// All stored submissions younger than SUBMISSION_TTL_MS, moving the old
// single-slot one into the map.
const readSubmissions = () => {
  const stored = readJSON(SUBMISSIONS_KEY) || {};
  const legacy = readJSON(LEGACY_SUBMISSION_KEY);
  if (legacy && legacy.params) {
    if (!legacy.autosave) legacy.autosave = autosaveForPending(legacy);
    stored[submissionKey(legacy.params)] = legacy;
    remove(LEGACY_SUBMISSION_KEY);
  }
  const all = {};
  Object.keys(stored).forEach((key) => {
    const p = stored[key];
    if (p && p.params && Date.now() - (p.saved_at || 0) < SUBMISSION_TTL_MS) {
      all[key] = p;
    }
  });
  if (legacy || Object.keys(all).length !== Object.keys(stored).length) {
    writeJSON(SUBMISSIONS_KEY, all);
  }
  return all;
};

export const savePendingSubmission = (params, uid, autosave) => {
  const all = readSubmissions();
  all[submissionKey(params)] = { params, uid, autosave, saved_at: Date.now() };
  writeJSON(SUBMISSIONS_KEY, all);
};

// The logged-in student's stored submissions, oldest first.
export const getPendingSubmissions = () => {
  const mobile = Cookies.get("mobile");
  return Object.values(readSubmissions())
    .filter((p) => p && p.params && p.params.mobile == mobile)
    .sort((a, b) => (a.saved_at || 0) - (b.saved_at || 0));
};

export const getPendingSubmission = () => getPendingSubmissions()[0] || null;

export const clearPendingSubmission = (params) => {
  const all = readSubmissions();
  delete all[submissionKey(params)];
  writeJSON(SUBMISSIONS_KEY, all);
};

// Device state of a pending exam that is now on the server. The progress slot
// and "uid" may already belong to an exam started since, so they are only
// reset when they are still this exam's.
const clearSubmittedExam = (pending) => {
  clearPendingSubmission(pending.params);
  const progress = readJSON(PROGRESS_KEY) || readJSON(LEGACY_AUTOSAVE_KEY);
  if (
    progress &&
    parseInt(progress.exam_paper_id) ===
      parseInt(pending.params.schedule_exam_id)
  ) {
    clearExamProgress();
  }
  if (localStorage.getItem("uid") === pending.uid) {
    localStorage.setItem("uid", 0);
  }
};

let syncInFlight = null;

// Answers to push to the server autosave: the copy stored with the submission,
// else the progress slot when it is the same exam.
const autosaveForPending = (pending) => {
  let autosave = pending.autosave;
  if (!autosave) {
    const progress = readJSON(PROGRESS_KEY) || readJSON(LEGACY_AUTOSAVE_KEY);
    if (
      progress &&
      parseInt(progress.exam_paper_id) ===
        parseInt(pending.params.schedule_exam_id)
    ) {
      autosave = progress;
    }
  }
  if (!autosave) return null;
  const { saved_at, ...params } = autosave;
  return params;
};

// Error codes from the response body. A 400 (e.g. schema validation) arrives
// as a networkError carrying the body in `result`.
const graphQLErrorCodes = (error) => {
  if (!error) return [];
  const network = error.networkError;
  const errors = [
    ...(error.graphQLErrors || []),
    ...((network && network.result && network.result.errors) || []),
  ];
  return errors.map((e) => e && e.extensions && e.extensions.code);
};

// The server refused the request on purpose (UserInputError, e.g. "already
// submitted"): retrying will never succeed. DB down, server crashes, auth and
// network failures are not rejections.
export const isRejectedByServer = (error) =>
  !isNetworkError(error) &&
  graphQLErrorCodes(error).includes("BAD_USER_INPUT");

// Resolves true when the server already has this exam submitted. Failures
// propagate (retry later), except a backend without this query, where the
// submit itself still rejects a duplicate.
const isAlreadySubmitted = (client, mobile, scheduleExamId) =>
  client
    .query({
      query: IS_SCHEDULE_EXAM_SUBMITTED,
      variables: { mobile, schedule_exam_id: parseInt(scheduleExamId) },
      fetchPolicy: "network-only",
    })
    .then(({ data }) => !!(data && data.isScheduleExamSubmitted))
    .catch((error) => {
      if (graphQLErrorCodes(error).includes("GRAPHQL_VALIDATION_FAILED")) {
        return false;
      }
      throw error;
    });

// Submits one stored offline exam:
//   0. if the server already has the exam submitted (e.g. auto-submitted while
//      the student was offline), nothing is sent: the device copy is removed,
//   1. update the server autosave with the answers saved on this device (the
//      server's own copy can be up to one autosave interval old),
//   2. submit the exam,
//   3. remove the device copy.
// Resolves to { exam_paper_id, sessionid, uid } when submitted,
// { exam_paper_id, alreadySubmitted: true } when skipped, otherwise null.
const syncOne = (client, pending) => {
  const { params, uid } = pending;
  const examPaperId = parseInt(params.schedule_exam_id);
  const autosave = autosaveForPending(pending);
  const updateAutosave = () =>
    autosave
      ? client
          .mutate({ mutation: AUTO_SAVE, variables: { params: autosave } })
          .catch((error) => {
            // No network: stop and retry later. Rejected by the server: still
            // submit, the submission carries the answers itself.
            if (isNetworkError(error)) throw error;
          })
      : Promise.resolve();
  const submit = () =>
    updateAutosave()
      .then(() =>
        client.mutate({
          mutation: SUBMIT_SCHEDULE_TEST,
          variables: { params, uid },
        }),
      )
      .then(({ data }) => onSubmitted(data));

  const onSubmitted = (data) => {
    const sessionid = data && data.finishStudentScheduleExam;
    if (!sessionid) return null;
    clearSubmittedExam(pending);
    client
      .mutate({
        mutation: GENERATE_EXAM_SESSION_RANKS,
        variables: { schedule_exam_id: examPaperId },
      })
      .catch((err) => {});
    const result = { exam_paper_id: examPaperId, sessionid, uid };
    window.dispatchEvent(
      new CustomEvent(OFFLINE_SUBMITTED_EVENT, { detail: result }),
    );
    return result;
  };

  const mobile = (autosave && autosave.mobile) || params.mobile;
  return isAlreadySubmitted(client, mobile, examPaperId)
    .then((alreadySubmitted) => {
      if (!alreadySubmitted) return submit();
      clearSubmittedExam(pending);
      const result = { exam_paper_id: examPaperId, alreadySubmitted: true };
      window.dispatchEvent(
        new CustomEvent(OFFLINE_SUBMITTED_EVENT, { detail: result }),
      );
      return result;
    })
    .catch((error) => {
      // Only a business rejection (already submitted, exam closed, ...) is
      // final. Anything else - no network, DB down, server error, auth -
      // keeps it for a retry until it expires (SUBMISSION_TTL_MS).
      if (isRejectedByServer(error)) clearSubmittedExam(pending);
      return null;
    });
};

// Submits the logged-in student's stored offline exams, oldest first. Safe to
// call repeatedly (e.g. on every `online` event); concurrent calls share one
// run. Resolves to the per-exam results of syncOne.
export const syncPendingSubmission = (client) => {
  if (syncInFlight) return syncInFlight;
  const pendings = getPendingSubmissions();
  if (pendings.length === 0 || !client) return Promise.resolve([]);
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return Promise.resolve([]);
  }
  syncInFlight = pendings
    .reduce(
      (chain, pending) =>
        chain.then((results) =>
          syncOne(client, pending).then((r) => [...results, r]),
        ),
      Promise.resolve([]),
    )
    .finally(() => {
      syncInFlight = null;
    });
  return syncInFlight;
};
